import {
	UseQueryResult,
	QueryStatus,
	CustomAxiosErrorType,
	updateUserAvatar,
	useClientAccountOverview,
	useCustomMailFolders,
	useUpdateAccountOverview,
} from '@ukpostbox/client-data';
import { AccountOverviewType, AccountUpdateType } from '@ukpostbox/client-data/lib/Types/AccountOverviewTypes';
import { CustomMailFolderType } from '@ukpostbox/client-data/lib/Types/FolderTypes';
import {
	AlertBanner,
	ButtonSubmit,
	ColourStateSolid,
	Modal,
	UploadFile,
	useLayout,
} from '@ukpostbox/frontend-framework';
import { Controller, FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useContext, useEffect, useMemo, useState } from 'react';

type UserProviderType = {
	userData?: AccountOverviewType;
	userDataStatus: QueryStatus;
	userDataError: CustomAxiosErrorType | null;

	userCustomFoldersData: UseQueryResult<Array<CustomMailFolderType>, Error | any>;

	SelectNewAvatar: () => void;
	UpdateAvatar: (file: File) => void;
};

const UserContext = React.createContext<UserProviderType>({} as UserProviderType);

function UserProvider(props: any) {
	const formMethods = useForm({
		defaultValues: {
			avatar: undefined,
		},
		mode: 'onChange',
	});

	const { setContentOnlyLayout, setDefaultLayout } = useLayout();

	const { data: userData, status: userDataStatus, error: userDataError } = useClientAccountOverview();
	const userCustomFoldersData = useCustomMailFolders();

	const updateAccountOverview = useUpdateAccountOverview();

	const [selectAvatarOpen, setSelectAvatarOpen] = useState<boolean>(false);
	const [avatarError, setAvatarError] = useState<any>(null);

	const SelectNewAvatar = () => {
		setSelectAvatarOpen(true);
	};

	const UpdateAvatar = (data: FieldValues) => {
		const formData = new FormData();

		formData.append('file', data.avatar[0], data.avatar[0].name);

		updateUserAvatar(formData)
			.then((r) => {
				if (r.url) {
					updateAccountOverview.mutate({
						newData: r.url,
						updateType: AccountUpdateType.AVATAR,
					});
				}
				setSelectAvatarOpen(false);
				setAvatarError(null);
			})
			.catch((ex) => {
				setAvatarError(ex);
				console.log(ex);
			});
	};

	const userMemo = useMemo(
		() => ({
			userData,
			userDataStatus,
			userDataError,

			userCustomFoldersData,

			SelectNewAvatar,
			UpdateAvatar,
		}),
		[userData, userDataStatus, userDataError, userCustomFoldersData],
	);

	useEffect(() => {
		if (userDataStatus === 'success') {
			console.log('user', userData);
		}
	}, [userDataStatus, userData]);

	useEffect(() => () => formMethods.reset(), []);

	return (
		<UserContext.Provider value={userMemo}>
			{props.children}

			<Modal title='Select Avatar' isOpen={selectAvatarOpen} closeCallback={() => setSelectAvatarOpen(false)}>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit((data) => UpdateAvatar(data))}>
						<fieldset>
							{avatarError && <AlertBanner title={avatarError.toString()} />}

							<Controller
								control={formMethods.control}
								name='avatar'
								rules={{ required: true }}
								render={({ field, fieldState }) => (
									<UploadFile
										name='Avatar'
										onError={(e) => null}
										extensionDisplayValue='images'
										acceptsFileExtension={['.png', '.jpg', '.jpeg', '.webp', '.gif']}
										onChange={(e) => field.onChange(e)}
										maxFiles={1}
									/>
								)}
							/>
							<ButtonSubmit
								title='Save'
								fullWidth
								colour={ColourStateSolid.Primary}
								disabled={!formMethods.formState.isValid}
							/>
						</fieldset>
					</form>
				</FormProvider>
			</Modal>
		</UserContext.Provider>
	);
}

const useUser = () => useContext(UserContext);

export { UserContext, useUser };

export default UserProvider;
